/**
 * <as-gui-ctrls> — turnkey Paramate GUI-controls (transform gizmo) integration.
 *
 * Put it NEXT TO <as-viewport>, not inside it:
 *
 *   <as-viewport></as-viewport>
 *   <as-gui-ctrls model-matrix-param="main:workpieceModelMtx" mode="translate"></as-gui-ctrls>
 *
 * Paramate's viewport replaces the contents of <as-viewport> with its own
 * canvases when it starts, so an element nested there is destroyed before it
 * ever runs. This element finds the viewport through the configurator, so
 * where it sits in the markup does not otherwise matter.
 *
 * The element owns a minimal Controls shim (see GizmoControlsShim below) and the
 * transform gizmo, so the host app never touches the Controls API and no Paramate
 * Controls library has to be loaded. The gizmo itself comes from a classic global
 * script the host page loads (same way as-configurator reads window.Paramate):
 *   - window.GUIControls.TransformGizmo   (/lib/GUIControls.js — the gizmo bundle)
 *
 * Gizmo commits go through the shared Paramate configurator, so undo/redo is picked
 * up by <as-configurator> automatically — one drag = one configure = one undo entry.
 * See docs/guiControls.md.
 *
 * Attributes:
 *   model-matrix-param  (required) trCAD param holding the workpiece instance model matrices
 *   world-matrix-param  (optional) trCAD param holding the default / world matrices
 *   mode                translate | rotate | scale   (default: translate)
 *   world-space         boolean flag — draw the gizmo in world space
 *   gizmo-size          number — gizmo handle size, in model units (live, or set .gizmoSize)
 *
 * Properties:
 *   gizmo               the TransformGizmo instance, once set up
 *   controls            the Controls shim
 *   options             TransformGizmo constructor options. Assignment merges,
 *                       so an app sets the one it needs (dragModeActive,
 *                       scaleUniform, scalingMin, ...) without naming the rest.
 *                       Read when the gizmo is built, so set them before the
 *                       configurator binds; the attributes above still apply.
 *   transform           the gizmo's input-driven transforms, for a value typed
 *                       into a box. Each returns the configure it starts, so
 *                       an app can hold it in a history batch:
 *     transform.translate(axis, value, idx?)   sets the position on one axis
 *     transform.rotate(axis, deg, idx?)        rotates by deg about one axis
 *     transform.scale(axis, value, idx?)       sets the scale on one axis
 *     transform.scaleUniform(value, idx?)      sets the scale on all axes
 *
 * translate(), rotate(), scale() and scaleUniform() on the element are
 * deprecated aliases of the transforms, removed in 0.18.
 */

import { devlog } from '../../util/utils.ts';
import { whenAsConfigurator } from '../../util/singletons.ts';
import { sendEv } from '../../data/events.ts';
import { warnDeprecated } from '../../util/deprecatedEl.ts';

export type GizmoAxis = 'x' | 'y' | 'z';

/** The transforms on `<as-gui-ctrls>.transform`. Each commits one configure
 *  and returns it. A transform the gizmo declines, an index with no instance
 *  for example, settles at once. */
export interface GizmoTransform {
  /** Sets the position on one axis. The other two stay where they are. */
  translate(axis: GizmoAxis, value: number, idx?: number): Promise<unknown>;
  /** Rotates by `deg` degrees about one axis, from the current orientation. */
  rotate(axis: GizmoAxis, deg: number, idx?: number): Promise<unknown>;
  /** Sets the scale on one axis, not a factor. Clamped to the `scalingMin` option. */
  scale(axis: GizmoAxis, value: number, idx?: number): Promise<unknown>;
  /** Sets the scale on all three axes. Clamped to the `scalingMin` option. */
  scaleUniform(value: number, idx?: number): Promise<unknown>;
}

/**
 * A stand-in for the live Paramate configurator, for handing to GUI controls.
 * Writes go through <as-configurator>: a control commits with
 * `controls.configurator.setParamValue(name, value)`, and on the raw SDK object
 * the third argument defaults to "configure now", so that call quietly starts
 * a rebuild the caller has no handle on. An app that then asks for a configure
 * of its own gets two overlapping rebuilds for one drag. Routing setParamValue
 * to the element instead means one configure, with the app's history and busy
 * state around it. Every other member passes straight through, because
 * controls read `.params`, `.viewPort` and friends directly.
 *
 * The target is resolved on each access, so the proxy can be built before
 * init() has produced the instance and never needs rebinding.
 *
 * `configure` is deliberately NOT routed: no shipped control calls it, and the
 * element's signature differs from the SDK's, so forwarding it would be a trap
 * rather than a convenience.
 */
function liveConfiguratorProxy(cfgEl: any, onCommit: (p: Promise<unknown>) => void): any {
  return new Proxy({}, {
    get(_target, prop) {
      const live = cfgEl.configurator;
      if (!live) return undefined;
      if (prop === 'setParamValue') {
        return (name: string, value: any, triggerConfigure?: boolean) => {
          const p = Promise.resolve(cfgEl.setParamValue(name, value, triggerConfigure ?? true));
          onCommit(p);
          return p;
        };
      }
      const member = Reflect.get(live, prop);
      return typeof member === 'function' ? member.bind(live) : member;
    },
    set(_target, prop, value) {
      const live = cfgEl.configurator;
      return live ? Reflect.set(live, prop, value) : false;
    },
    has(_target, prop) {
      const live = cfgEl.configurator;
      return live ? Reflect.has(live, prop) : false;
    },
  });
}

/**
 * Minimal Controls shim.
 *
 * The Paramate transform gizmo only needs three things from a "Controls" object:
 *   1. `.configurator`  — it reads params / setParamValue / debug through it.
 *   2. register / deregister so it is tracked as an "external control".
 *   3. `.updateControls()` — the configurator calls this after every
 *      configure/initialize/restore; it loops the external controls and calls
 *      `.update()` on each, which re-pulls the gizmo's matrix and keeps it in sync
 *      after an undo/redo re-applies the param.
 * The gizmo binds its OWN viewport pointer events, so no pointer routing is needed here.
 *
 * This mirrors only the essential surface of Paramate's real `Controls`
 * (lib-custom/paramateControls.js) without pulling that library in.
 *
 * If the full Controls behaviour is ever needed (HTML paramate-* inputs,
 * file-upload handling, ray-select, services) the shim can be swapped for the
 * real lib: `new (window as any).Paramate.Controls({ configurator })` after
 * loading paramateControls.js. The gizmo wiring in _setup() is the same either
 * way: constructor + registerExternalControl + updateControls. The shim avoids
 * the extra global dependency and keeps the app free of the Controls API.
 */
class GizmoControlsShim {
  configurator: any;
  externalControls: any[] = [];
  // Benign fields the real Controls exposes; some Paramate code paths read them.
  name = 'as-gui-ctrls';
  services: Record<string, unknown> = {};
  fileUploads: any[] = [];
  onBeforeUpdateControls: (() => boolean) | undefined;

  constructor(configurator: any) {
    this.configurator = configurator;
  }

  registerExternalControl(ctrl: any): void {
    if (!this.externalControls.includes(ctrl)) this.externalControls.push(ctrl);
  }

  deregisterExternalControl(ctrl: any): void {
    const i = this.externalControls.indexOf(ctrl);
    if (i !== -1) this.externalControls.splice(i, 1);
  }

  /** Called by the configurator after each configure — re-syncs every external control. */
  updateControls(): void {
    if (this.onBeforeUpdateControls && !this.onBeforeUpdateControls()) return;
    for (const ctrl of this.externalControls) ctrl.update?.();
  }
}

export class AsGuiCtrls extends HTMLElement {
  static tag = 'as-gui-ctrls';

  private _gizmo: any = null;
  private _controls: GizmoControlsShim | any = null;
  private _cfgEl: any = null;
  private _ownsControls = false;
  private _ready = false;
  /** The configure the gizmo started last, caught at the proxy. */
  private _lastConfigure: Promise<unknown> = Promise.resolve();
  private _options: Record<string, unknown> = {};

  /** The gizmo's input-driven transforms. See GizmoTransform. */
  readonly transform: GizmoTransform = {
    translate: (axis, value, idx = 0) => this._commit(g => g.translateOnInputChange(axis, value, idx)),
    rotate: (axis, deg, idx = 0) => this._commit(g => g.rotateOnInputChange(axis, deg, idx)),
    scale: (axis, value, idx = 0) => this._commit(g => g.scaleOnInputChange(axis, value, idx)),
    scaleUniform: (value, idx = 0) => this._commit(g => g.scaleUniformOnInputChange(value, idx)),
  };

  static get observedAttributes() { return ['mode', 'gizmo-size']; }

  /** The underlying gizmo instance (once set up), or null. */
  get gizmo() { return this._gizmo; }
  /** The Controls shim this element manages, or null. */
  get controls() { return this._controls; }
  /** TransformGizmo constructor options, merged in on assignment and read when
   *  the gizmo is built. The mode, world-space and gizmo-size attributes are
   *  applied on top, so they keep working as documented. */
  get options(): Record<string, unknown> { return this._options; }
  set options(v: Record<string, unknown>) { this._options = { ...this._options, ...v }; }

  /** @deprecated use `transform.translate()`. It shadows the DOM's
   *  `translate` property, a boolean, which is why the name moved. */
  // @ts-expect-error shadows HTMLElement.translate, which is a boolean
  translate(axis: GizmoAxis, value: number, idx = 0): Promise<unknown> {
    warnDeprecated('as-gui-ctrls.translate', '[DEPRECATED] as-gui-ctrls translate() is removed in 0.18. Use transform.translate().');
    return this.transform.translate(axis, value, idx);
  }
  /** @deprecated use `transform.rotate()`. */
  rotate(axis: GizmoAxis, deg: number, idx = 0): Promise<unknown> {
    warnDeprecated('as-gui-ctrls.rotate', '[DEPRECATED] as-gui-ctrls rotate() is removed in 0.18. Use transform.rotate().');
    return this.transform.rotate(axis, deg, idx);
  }
  /** @deprecated use `transform.scale()`. */
  scale(axis: GizmoAxis, value: number, idx = 0): Promise<unknown> {
    warnDeprecated('as-gui-ctrls.scale', '[DEPRECATED] as-gui-ctrls scale() is removed in 0.18. Use transform.scale().');
    return this.transform.scale(axis, value, idx);
  }
  /** @deprecated use `transform.scaleUniform()`. */
  scaleUniform(value: number, idx = 0): Promise<unknown> {
    warnDeprecated('as-gui-ctrls.scaleUniform', '[DEPRECATED] as-gui-ctrls scaleUniform() is removed in 0.18. Use transform.scaleUniform().');
    return this.transform.scaleUniform(value, idx);
  }

  /** Runs one gizmo transform and returns the configure it started. The gizmo
   *  commits synchronously through the proxy, so if nothing new was caught it
   *  declined the transform and there is nothing to wait for. */
  private _commit(call: (gizmo: any) => void): Promise<unknown> {
    if (!this._gizmo) return Promise.reject(new Error('[as-gui-ctrls] the gizmo is not set up yet'));
    const before = this._lastConfigure;
    call(this._gizmo);
    return this._lastConfigure === before ? Promise.resolve() : this._lastConfigure;
  }

  connectedCallback() {
    this.style.display = 'none';
    devlog('[as-gui-ctrls] connected — waiting for configurator');
    void this._init();
  }

  disconnectedCallback() {
    this._teardown();
  }

  attributeChangedCallback(name: string, _old: string | null, val: string | null) {
    if (!this._gizmo) return;
    if (name === 'mode' && val) this._gizmo.mode = val;
    if (name === 'gizmo-size') this._applyGizmoSize(val);
  }

  /** Switch the active gizmo mode. */
  setMode(mode: 'translate' | 'rotate' | 'scale') { this.setAttribute('mode', mode); }

  /**
   * Gizmo handle size, in model units - the distance from the gizmo's origin
   * to each handle tip, so it has to suit whatever the gizmo sits on. Live: set
   * it whenever that thing changes scale, e.g. from a trCAD param holding an
   * imported part's longest side.
   *
   * A mesh centred on its own bbox reaches HALF its longest side out from the
   * gizmo, so the handles only clear it above 0.5 of that side. Under it they
   * are drawn inside the part, where they cannot be seen or easily grabbed:
   *
   *   guiCtrls.gizmoSize = partSize * 0.6;   // half, plus a margin
   *
   * The same thing as the `gizmo-size` attribute, which is where it is stored.
   * Null when it has never been set, and the gizmo then draws at its own
   * default of 5.
   */
  get gizmoSize(): number | null {
    const attr = this.getAttribute('gizmo-size');
    return attr === null ? null : Number(attr);
  }

  set gizmoSize(value: number | null) {
    if (value == null) this.removeAttribute('gizmo-size');
    else this.setAttribute('gizmo-size', String(value));
  }

  /** The gizmo ignores a size of zero or less, so anything unusable leaves it
   *  on the size it already has rather than collapsing the handles. */
  private _applyGizmoSize(val: string | null): void {
    const size = Number(val);
    if (val === null || !Number.isFinite(size) || size <= 0) return;
    this._gizmo.size = size;
    this._gizmo.update?.();
  }

  private async _init(): Promise<void> {
    // Resolves once <as-configurator> has connected, so it is upgraded and
    // waitForConfigurator() exists. No polling needed.
    const cfgEl = await whenAsConfigurator();
    if (!this.isConnected || this._ready) return;
    this._cfgEl = cfgEl;
    const configurator = await cfgEl.waitForConfigurator();
    if (!this.isConnected || this._ready) return;
    devlog('[as-gui-ctrls] configurator received');
    this._whenViewport(configurator, (c) => this._setup(c));
  }

  /** The configurator can answer a tick before its viewPort is attached; wait for it. */
  private _whenViewport(configurator: any, cb: (c: any) => void): void {
    if (configurator.viewPort) { cb(configurator); return; }
    let tries = 0;
    const poll = setInterval(() => {
      if (this._ready || !this.isConnected) { clearInterval(poll); return; }
      if (configurator.viewPort) { clearInterval(poll); cb(configurator); }
      else if (++tries > 50) { clearInterval(poll); devlog('[as-gui-ctrls] configurator has no viewPort — cannot set up gizmo'); }
    }, 120);
  }

  private _setup(configurator: any): void {
    const GUIControls = (window as any).GUIControls;
    if (!GUIControls?.TransformGizmo) {
      devlog('[as-gui-ctrls] window.GUIControls not found — load /lib/GUIControls.js');
      return;
    }

    const modelParam = this.getAttribute('model-matrix-param');
    if (!modelParam) {
      devlog('[as-gui-ctrls] the model-matrix-param attribute is required');
      return;
    }
    const worldParam = this.getAttribute('world-matrix-param') || undefined;

    // Own a Controls shim so the app never has to — reuse one if already attached.
    if (configurator.controls) {
      this._controls = configurator.controls;
    } else {
      this._controls = new GizmoControlsShim(liveConfiguratorProxy(this._cfgEl, (p) => { this._lastConfigure = p; }));
      configurator.controls = this._controls; // the configurator drives updateControls() on this
      this._ownsControls = true;
    }

    const options: Record<string, unknown> = {
      hasChildren: false,
      scaleActive: false,
      ...this._options,
      worldSpace: this.hasAttribute('world-space'),
    };
    const sizeAttr = this.getAttribute('gizmo-size');
    if (sizeAttr) options.gizmoSize = Number(sizeAttr);

    this._gizmo = new GUIControls.TransformGizmo(
      configurator.viewPort,
      this._controls,
      options,
      modelParam,
      worldParam,
    );
    this._gizmo.mode = this.getAttribute('mode') || 'translate';
    this._gizmo.show();
    this._ready = true;
    devlog(`[as-gui-ctrls] gizmo ready (numGizmos=${this._gizmo.numGizmos})`);

    // A gizmo draws one handle-set per matrix in the model param. If the param
    // doesn't exist on the configurator (or is empty) there is nothing to draw —
    // surface that instead of a silently invisible gizmo.
    if (this._gizmo.numGizmos === 0) {
      devlog(
        `[as-gui-ctrls] gizmo has 0 instances — model-matrix-param "${modelParam}" is ` +
        `missing on the configurator or holds no matrices, so nothing will be drawn. ` +
        `Point it at a trCAD param that returns the serialized instance matrices (>= 1).`,
      );
    }

    sendEv(this, 'as-gui-ctrls-ready', {
      detail: { gizmo: this._gizmo, controls: this._controls }
    });
  }

  private _teardown(): void {
    try { this._gizmo?.hide?.(); } catch { /* ignore */ }
    if (this._ownsControls && this._controls?.deregisterExternalControl && this._gizmo) {
      try { this._controls.deregisterExternalControl(this._gizmo); } catch { /* ignore */ }
    }
    this._gizmo = null;
    this._ready = false;
  }
}

if (!customElements.get(AsGuiCtrls.tag)) {
  // The cast goes with the shadowed translate above: the constructor no longer
  // matches CustomElementConstructor by type, only by shape.
  customElements.define(AsGuiCtrls.tag, AsGuiCtrls as unknown as CustomElementConstructor);
}
